'use client'

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

export type PersistentStatus = 'idle' | 'loading' | 'success' | 'error'

export interface PersistentQuerySnapshot<T> {
    data: T | undefined
    error: Error | null
    status: PersistentStatus
    updatedAt: number
}

interface QueryEntry<T> {
    snapshot: PersistentQuerySnapshot<T>
    listeners: Set<() => void>
    promise?: Promise<void>
}

export interface ProgressiveListSnapshot<T> {
    items: T[]
    error: Error | null
    status: PersistentStatus
    updatedAt: number
}

interface ProgressiveEntry<T> {
    snapshot: ProgressiveListSnapshot<T>
    listeners: Set<() => void>
    promise?: Promise<void>
}

const EMPTY_QUERY: PersistentQuerySnapshot<never> = {
    data: undefined,
    error: null,
    status: 'idle',
    updatedAt: 0
}
const EMPTY_LIST: ProgressiveListSnapshot<never> = {
    items: [],
    error: null,
    status: 'idle',
    updatedAt: 0
}

// Module-owned promises are intentionally independent of component lifetime.
// A route can unmount while its request continues; the next mount subscribes to
// the same entry and receives the latest partial or complete result immediately.
const queryCache = new Map<string, QueryEntry<unknown>>()
const progressiveCache = new Map<string, ProgressiveEntry<unknown>>()
const MAX_CACHE_ENTRIES = 150

function toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error))
}

function pruneCache(
    cache: Map<string, { listeners: Set<() => void>; promise?: Promise<void>; snapshot: { updatedAt: number } }>
) {
    if (cache.size <= MAX_CACHE_ENTRIES) return

    const removable = [...cache.entries()]
        .filter(([, entry]) => entry.listeners.size === 0 && !entry.promise)
        .sort((a, b) => a[1].snapshot.updatedAt - b[1].snapshot.updatedAt)

    for (const [key] of removable.slice(0, cache.size - MAX_CACHE_ENTRIES)) cache.delete(key)
}

function getQueryEntry<T>(key: string): QueryEntry<T> {
    let entry = queryCache.get(key) as QueryEntry<T> | undefined
    if (!entry) {
        entry = { snapshot: { ...EMPTY_QUERY }, listeners: new Set() }
        queryCache.set(key, entry as QueryEntry<unknown>)
        pruneCache(queryCache)
    }
    return entry
}

function publishQuery<T>(entry: QueryEntry<T>, snapshot: PersistentQuerySnapshot<T>) {
    entry.snapshot = snapshot
    entry.listeners.forEach(listener => listener())
}

function startQuery<T>(key: string, queryFn: () => Promise<T>, staleTimeMs: number, force = false) {
    const entry = getQueryEntry<T>(key)
    const isFresh = Date.now() - entry.snapshot.updatedAt < staleTimeMs
    if (entry.promise || (!force && entry.snapshot.status === 'success' && isFresh)) return

    publishQuery(entry, { ...entry.snapshot, status: 'loading', error: null })
    entry.promise = queryFn()
        .then(data => publishQuery(entry, { data, error: null, status: 'success', updatedAt: Date.now() }))
        .catch(error => publishQuery(entry, { ...entry.snapshot, error: toError(error), status: 'error' }))
        .finally(() => {
            entry.promise = undefined
        })
}

/**
 * Imperative companion for existing client effects. It deduplicates requests and
 * keeps the promise/result alive when the component that started it unmounts.
 */
export async function persistentQuery<T>(
    cacheKey: string,
    queryFn: () => Promise<T>,
    staleTimeMs = 5 * 60 * 1000
): Promise<T> {
    const entry = getQueryEntry<T>(cacheKey)
    startQuery(cacheKey, queryFn, staleTimeMs)
    const pending = entry.promise
    if (pending) await pending
    if (entry.snapshot.status === 'error') throw entry.snapshot.error
    return entry.snapshot.data as T
}

export function usePersistentQuery<T>({
    cacheKey,
    queryFn,
    enabled = true,
    staleTimeMs = 5 * 60 * 1000
}: {
    cacheKey: string
    queryFn: () => Promise<T>
    enabled?: boolean
    staleTimeMs?: number
}): PersistentQuerySnapshot<T> & { refresh: () => void } {
    const queryFnRef = useRef(queryFn)
    queryFnRef.current = queryFn

    const subscribe = useCallback(
        (listener: () => void) => {
            const entry = getQueryEntry<T>(cacheKey)
            entry.listeners.add(listener)
            return () => entry.listeners.delete(listener)
        },
        [cacheKey]
    )
    const getSnapshot = useCallback(() => getQueryEntry<T>(cacheKey).snapshot, [cacheKey])
    const getServerSnapshot = useCallback(() => EMPTY_QUERY as PersistentQuerySnapshot<T>, [])
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

    useEffect(() => {
        if (enabled) startQuery(cacheKey, () => queryFnRef.current(), staleTimeMs)
    }, [cacheKey, enabled, staleTimeMs])

    const refresh = useCallback(() => {
        if (enabled) startQuery(cacheKey, () => queryFnRef.current(), staleTimeMs, true)
    }, [cacheKey, enabled, staleTimeMs])

    return { ...snapshot, refresh }
}

function getProgressiveEntry<T>(key: string): ProgressiveEntry<T> {
    let entry = progressiveCache.get(key) as ProgressiveEntry<T> | undefined
    if (!entry) {
        entry = { snapshot: { ...EMPTY_LIST, items: [] }, listeners: new Set() }
        progressiveCache.set(key, entry as ProgressiveEntry<unknown>)
        pruneCache(progressiveCache)
    }
    return entry
}

function publishProgress<T>(entry: ProgressiveEntry<T>, snapshot: ProgressiveListSnapshot<T>) {
    entry.snapshot = snapshot
    entry.listeners.forEach(listener => listener())
}

function startProgressiveLoad<T>(
    key: string,
    total: number,
    batchSize: number,
    loadBatch: (offset: number, limit: number) => Promise<T[]>
) {
    const entry = getProgressiveEntry<T>(key)
    if (entry.promise || entry.snapshot.items.length >= total) return

    publishProgress(entry, { ...entry.snapshot, status: 'loading', error: null })
    entry.promise = (async () => {
        while (entry.snapshot.items.length < total) {
            const offset = entry.snapshot.items.length
            const items = await loadBatch(offset, Math.min(batchSize, total - offset))
            if (items.length === 0) break
            publishProgress(entry, {
                items: [...entry.snapshot.items, ...items],
                error: null,
                status: 'loading',
                updatedAt: Date.now()
            })
        }
        publishProgress(entry, { ...entry.snapshot, status: 'success', updatedAt: Date.now() })
    })()
        .catch(error => publishProgress(entry, { ...entry.snapshot, error: toError(error), status: 'error' }))
        .finally(() => {
            entry.promise = undefined
        })
}

export function usePersistentProgressiveList<T>({
    cacheKey,
    total,
    loadBatch,
    batchSize = 10,
    enabled = true
}: {
    cacheKey: string
    total: number
    loadBatch: (offset: number, limit: number) => Promise<T[]>
    batchSize?: number
    enabled?: boolean
}): ProgressiveListSnapshot<T> & { retry: () => void } {
    const loadBatchRef = useRef(loadBatch)
    loadBatchRef.current = loadBatch

    const subscribe = useCallback(
        (listener: () => void) => {
            const entry = getProgressiveEntry<T>(cacheKey)
            entry.listeners.add(listener)
            return () => entry.listeners.delete(listener)
        },
        [cacheKey]
    )
    const getSnapshot = useCallback(() => getProgressiveEntry<T>(cacheKey).snapshot, [cacheKey])
    const getServerSnapshot = useCallback(() => EMPTY_LIST as ProgressiveListSnapshot<T>, [])
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

    useEffect(() => {
        if (enabled && total > 0) {
            startProgressiveLoad(cacheKey, total, batchSize, (offset, limit) => loadBatchRef.current(offset, limit))
        }
    }, [batchSize, cacheKey, enabled, total])

    const retry = useCallback(() => {
        if (enabled && total > 0) {
            startProgressiveLoad(cacheKey, total, batchSize, (offset, limit) => loadBatchRef.current(offset, limit))
        }
    }, [batchSize, cacheKey, enabled, total])

    return { ...snapshot, retry }
}
